import { capitalizeWords } from '@elementary/common-ui/utils/common';
import {
  JsonSchema,
  JsonSchemaType,
  YAML_FORM_SKIP_KEYS,
  YamlFormField,
  YamlFormFieldType,
  YamlFormOptionSource,
} from './types';

const TABLE_FIELD_NAMES = new Set(['compare_model', 'to']);

const TIMESTAMP_FIELD_NAMES = new Set([
  'timestamp_column',
  'event_timestamp_column',
  'update_timestamp_column',
  'date_column_name',
]);

const COLUMN_FIELD_NAMES = new Set([
  'field',
  'column_name',
  'compare_column_name',
  'sort_column',
  'lower_bound_column',
  'upper_bound_column',
  'dimensions',
  'group_by',
  'group_by_columns',
  'compare_group_by',
  'combination_of_columns',
  'compare_columns',
  'exclude_columns',
  'column_list',
]);

export const getOptionSource = (
  name: string,
): YamlFormOptionSource | undefined => {
  if (TABLE_FIELD_NAMES.has(name)) {
    return 'tables';
  }
  if (TIMESTAMP_FIELD_NAMES.has(name)) {
    return 'timestampColumns';
  }
  if (
    COLUMN_FIELD_NAMES.has(name) ||
    name.endsWith('_column') ||
    name.endsWith('_columns') ||
    name.endsWith('_column_name')
  ) {
    return 'columns';
  }
  return undefined;
};

const getPrimaryType = (schema: JsonSchema): JsonSchemaType | undefined => {
  if (Array.isArray(schema.type)) {
    return schema.type.find((schemaType) => schemaType !== 'null');
  }
  return schema.type;
};

const getStringEnum = (schema: JsonSchema): string[] | undefined => {
  if (!schema.enum || schema.enum.length === 0) {
    return undefined;
  }
  if (
    schema.enum.every((value): value is string => typeof value === 'string')
  ) {
    return schema.enum;
  }
  return undefined;
};

const getFieldType = (schema: JsonSchema): YamlFormFieldType => {
  const schemaType = getPrimaryType(schema);
  const enumValues = getStringEnum(schema);

  if (enumValues && (schemaType === 'string' || schemaType === undefined)) {
    return 'autocomplete';
  }
  if (schemaType === 'string') {
    return 'text';
  }
  if (schemaType === 'integer') {
    return 'numeric';
  }
  if (schemaType === 'number') {
    return 'decimal';
  }
  if (schemaType === 'boolean') {
    return 'boolean';
  }
  if (schemaType === 'array') {
    const itemSchema = schema.items;
    if (itemSchema && getStringEnum(itemSchema)) {
      return 'multiAutocomplete';
    }
    if (!itemSchema || getPrimaryType(itemSchema) === 'string') {
      return 'stringList';
    }
    return 'json';
  }
  if (
    schema.properties &&
    (schemaType === 'object' || schemaType === undefined)
  ) {
    return 'object';
  }
  return 'text';
};

const getEnumValuesForField = (
  schema: JsonSchema,
  fieldType: YamlFormFieldType,
): string[] | undefined => {
  if (fieldType === 'autocomplete') {
    return getStringEnum(schema);
  }
  if (fieldType === 'multiAutocomplete' && schema.items) {
    return getStringEnum(schema.items);
  }
  return undefined;
};

export const schemaToFormFields = (
  schema: JsonSchema,
  path: string[] = [],
): YamlFormField[] => {
  const properties = schema.properties;
  if (!properties) {
    return [];
  }
  const required = new Set(schema.required ?? []);

  return Object.entries(properties)
    .filter(([name]) => !YAML_FORM_SKIP_KEYS.includes(name))
    .map(([name, propertySchema]) => {
      const fieldPath = [...path, name];
      let fieldType = getFieldType(propertySchema);
      const enumValues = getEnumValuesForField(propertySchema, fieldType);
      const optionSource = enumValues ? undefined : getOptionSource(name);
      if (optionSource && fieldType === 'text') {
        fieldType = 'autocomplete';
      }
      if (optionSource && fieldType === 'stringList') {
        fieldType = 'multiAutocomplete';
      }
      return {
        name,
        path: fieldPath,
        label: capitalizeWords(name.replaceAll('_', ' ')),
        description: propertySchema.description,
        fieldType,
        required: required.has(name),
        ...(enumValues ? { enumValues } : {}),
        ...(optionSource &&
        (fieldType === 'autocomplete' || fieldType === 'multiAutocomplete')
          ? { optionSource }
          : {}),
        ...(fieldType === 'object'
          ? { children: schemaToFormFields(propertySchema, fieldPath) }
          : {}),
      };
    });
};
